"use client";

import { memo, useMemo, useEffect, useRef, useState, type ReactNode } from "react";
import { AttachFileActions } from "./attach-file-actions";
import { AttachPreviewImage } from "@/components/admin/attach-picker/attach-preview-image";
import { useTranslations } from "next-intl";
import {
  FileArchiveIcon,
  FileAudioIcon,
  FileCodeIcon,
  FileIcon,
  FileSpreadsheetIcon,
  FileTextIcon,
  FileVideoIcon,
  PresentationIcon,
} from "lucide-react";

import { Skeleton } from "@/components/ui/skeleton";
import { AttachPickTooltip } from "@/components/admin/attach-picker/attach-pick-tooltip";
import type { AttachFileRecord } from "@/components/admin/attach-picker/types";
import { AttachUploadTile } from "@/components/admin/attach-picker/attach-upload-tile";
import {
  formatAttachMeta,
  isImageMime,
} from "@/components/admin/attach-picker/utils";
import type { AttachUploadTask } from "@/lib/upload/attach-upload-client";
import { cn } from "@/lib/utils";
import { HoverTip } from "@/components/ui/tooltip";

export const ATTACH_GRID_VISIBLE_SLOTS = 10;

const ATTACH_GRID_CLASS = "grid grid-cols-5 gap-x-3 gap-y-3";

function AttachGridHeightAnchor() {
  return (
    <div
      className={cn(ATTACH_GRID_CLASS, "invisible select-none")}
      aria-hidden
    >
      {Array.from({ length: ATTACH_GRID_VISIBLE_SLOTS }, (_, index) => (
        <div key={index} className="flex flex-col">
          <div className="aspect-square w-full" />
          <p className="mt-1.5 h-4 shrink-0" />
        </div>
      ))}
    </div>
  );
}

export function AttachFileGridSkeleton() {
  return (
    <div className={ATTACH_GRID_CLASS}>
      {Array.from({ length: ATTACH_GRID_VISIBLE_SLOTS }, (_, index) => (
        <div key={index} className="flex flex-col">
          <Skeleton className="aspect-square w-full rounded-lg bg-muted/40" />
          <Skeleton className="mx-auto mt-1.5 h-4 w-2/3 rounded bg-muted/30" />
        </div>
      ))}
    </div>
  );
}

export function AttachFileGridViewport({
  children,
  emptyOverlay,
  loading,
}: {
  children: ReactNode;
  emptyOverlay?: ReactNode;
  loading?: boolean;
}) {
  return (
    <div className="relative min-h-0 px-4 pb-4">
      <AttachGridHeightAnchor />
      <div className="absolute inset-x-4 top-0 bottom-4 overflow-y-auto [scrollbar-gutter:stable]">
        {loading ? <AttachFileGridSkeleton /> : children}
      </div>
      {!loading && emptyOverlay ? (
        <div className="pointer-events-none absolute inset-x-4 top-0 bottom-4 flex items-center justify-center bg-background/80 text-sm text-muted-foreground">
          {emptyOverlay}
        </div>
      ) : null}
    </div>
  );
}

function AttachFileIcon({ mimeType }: { mimeType: string | null }) {
  if (mimeType?.startsWith("video/")) {
    return <FileVideoIcon className="size-10 text-muted-foreground" />;
  }

  if (mimeType?.startsWith("audio/")) {
    return <FileAudioIcon className="size-10 text-muted-foreground" />;
  }

  if (mimeType?.includes("pdf") || mimeType?.includes("word")) {
    return <FileTextIcon className="size-10 text-muted-foreground" />;
  }

  if (
    mimeType?.includes("sheet") ||
    mimeType?.includes("excel") ||
    mimeType?.includes("csv")
  ) {
    return <FileSpreadsheetIcon className="size-10 text-muted-foreground" />;
  }

  if (mimeType?.includes("presentation") || mimeType?.includes("powerpoint")) {
    return <PresentationIcon className="size-10 text-muted-foreground" />;
  }

  if (
    mimeType?.includes("zip") ||
    mimeType?.includes("rar") ||
    mimeType?.includes("7z")
  ) {
    return <FileArchiveIcon className="size-10 text-muted-foreground" />;
  }

  if (mimeType?.startsWith("text/") || mimeType?.includes("javascript")) {
    return <FileCodeIcon className="size-10 text-muted-foreground" />;
  }

  return <FileIcon className="size-10 text-muted-foreground" />;
}

function AttachPlaceholderCell() {
  return (
    <div className="flex flex-col">
      <div className="aspect-square w-full rounded-lg border border-dashed border-border/40 bg-muted/10" />
      <p className="mt-1.5 h-4 shrink-0" aria-hidden />
    </div>
  );
}

const attachNameLabelClass =
  "mt-1.5 box-border h-4 max-h-4 min-h-4 w-full min-w-0 shrink-0 truncate py-0 text-center text-xs leading-4 text-muted-foreground";

function AttachFileCell({
  file,
  checked,
  picked,
  selectionVisible,
  disabled,
  owned = true,
  onToggleSelect,
  onPick,
  onDelete,
  onReplace,
  onMove,
  onRename,
}: {
  file: AttachFileRecord;
  checked: boolean;
  picked: boolean;
  selectionVisible: boolean;
  disabled?: boolean;
  owned?: boolean;
  onToggleSelect: (id: string) => void;
  onPick: (file: AttachFileRecord) => void;
  onDelete: (id: string) => void;
  onReplace?: (file: AttachFileRecord) => void;
  onMove: (file: AttachFileRecord) => void;
  onRename?: (id: string, originalName: string) => void;
}) {
  const t = useTranslations("site.attach");
  const showImage = isImageMime(file.mimeType);
  const canRename = Boolean(owned && onRename && !disabled);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(file.originalName);
  const renameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setDraft(file.originalName);
  }, [file.originalName]);

  useEffect(() => {
    if (!editing || !renameInputRef.current) {
      return;
    }

    const input = renameInputRef.current;
    input.focus();
    const end = input.value.length;
    input.setSelectionRange(end, end);
  }, [editing]);

  function cancelRename() {
    setDraft(file.originalName);
    setEditing(false);
  }

  function commitRename() {
    if (!canRename || !onRename) {
      cancelRename();
      return;
    }

    const next = draft.trim();

    if (!next || next === file.originalName) {
      cancelRename();
      return;
    }

    onRename(file.id, next);
    setEditing(false);
  }

  return (
    <div className="flex flex-col">
      <div
        className={cn(
          "group/file relative aspect-square w-full overflow-hidden rounded-lg border bg-background transition-colors",
          picked && "border-primary ring-2 ring-primary/20",
          !picked && checked && "border-primary/40",
        )}
      >
        <label
          className={cn(
            "absolute top-1.5 left-1.5 z-20 transition-opacity",
            selectionVisible || checked
              ? "opacity-100"
              : "opacity-0 group-hover/file:opacity-100",
          )}
        >
          <input
            type="checkbox"
            checked={checked}
            disabled={disabled}
            className="size-4 cursor-pointer rounded border-input bg-background/90"
            onChange={() => onToggleSelect(file.id)}
            onClick={(event) => event.stopPropagation()}
          />
        </label>

        <AttachPickTooltip url={file.url} disabled={actionsOpen}>
          <button
            type="button"
            disabled={disabled}
            onClick={() => onPick(file)}
            className="relative flex size-full cursor-pointer items-center justify-center overflow-hidden p-0"
          >
            {showImage ? (
              <AttachPreviewImage src={file.thumbUrl ?? file.url} fallback={file.url} alt={file.originalName} />
            ) : (
              <AttachFileIcon mimeType={file.mimeType} />
            )}
          </button>
        </AttachPickTooltip>

        <div
          className={cn(
            "absolute top-1.5 right-1.5 z-20 opacity-0 transition-opacity group-hover/file:opacity-100 group-focus-within/file:opacity-100",
            actionsOpen && "opacity-100",
          )}
          onClick={(event) => event.stopPropagation()}
        >
          <AttachFileActions
            file={file}
            disabled={disabled}
            className="size-4 rounded-none bg-transparent hover:bg-transparent"
            onOpenChange={setActionsOpen}
            onReplace={onReplace}
            onMove={onMove}
            onDelete={onDelete}
          />
        </div>

        <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-black/55 px-1.5 py-1 text-center text-[11px] text-white opacity-0 transition-opacity group-hover/file:opacity-100">
          {formatAttachMeta(file)}
        </div>
      </div>
      {canRename && editing ? (
        <input
          autoComplete="off"
          ref={renameInputRef}
          value={draft}
          spellCheck={false}
          className={cn(
            attachNameLabelClass,
            "cursor-text border-0 bg-transparent p-0 shadow-none outline-none focus-visible:ring-0",
          )}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commitRename}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              commitRename();
            }

            if (event.key === "Escape") {
              event.preventDefault();
              cancelRename();
            }
          }}
          onClick={(event) => event.stopPropagation()}
        />
      ) : (
        <HoverTip content={canRename ? t("doubleClickRename") : file.originalName}><p
          className={cn(
            attachNameLabelClass,
            canRename && "cursor-text hover:text-foreground",
          )}
          onDoubleClick={(event) => {
            if (!canRename) {
              return;
            }

            event.stopPropagation();
            setEditing(true);
          }}
        >
          {file.originalName}
        </p></HoverTip>
      )}
    </div>
  );
}

const MemoAttachFileCell = memo(AttachFileCell);

function AttachUploadTaskCell({
  task,
  onCancel,
}: {
  task: AttachUploadTask;
  onCancel: (taskId: string) => void;
}) {
  return (
    <div className="flex flex-col">
      <AttachUploadTile
        previewUrl={task.previewUrl}
        mimeType={task.file.type || null}
        progress={task.progress}
        phase={task.phase}
        status={task.status === "cancelled" ? "uploading" : task.status}
        error={task.error}
        onRemove={() => onCancel(task.id)}
      />
      <HoverTip content={task.error ?? task.file.name}><p
        className={cn(
          attachNameLabelClass,
          task.status === "error" && "text-destructive",
        )}
      >
        {task.file.name}
      </p></HoverTip>
    </div>
  );
}

export function AttachFileGrid({
  items,
  uploadTasks = [],
  selectedIds,
  pickedId,
  selectionVisible,
  onToggleSelect,
  onPick,
  onDelete,
  onReplace,
  onMove,
  onRename,
  onCancelUpload,
  disabled,
}: {
  items: AttachFileRecord[];
  uploadTasks?: AttachUploadTask[];
  selectedIds: string[];
  pickedId: string | null;
  selectionVisible: boolean;
  onToggleSelect: (id: string) => void;
  onPick: (file: AttachFileRecord) => void;
  onDelete: (id: string) => void;
  onReplace?: (file: AttachFileRecord) => void;
  onMove: (file: AttachFileRecord) => void;
  onRename?: (id: string, originalName: string) => void;
  onCancelUpload?: (taskId: string) => void;
  disabled?: boolean;
}) {
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const visibleUploadTasks = uploadTasks.filter(
    (task) => task.status !== "cancelled",
  );
  const mergedCount = visibleUploadTasks.length + items.length;
  const showAllWithScroll = mergedCount > ATTACH_GRID_VISIBLE_SLOTS;

  const slots = showAllWithScroll
    ? [
        ...visibleUploadTasks.map((task) => ({
          type: "upload" as const,
          key: task.id,
          task,
        })),
        ...items.map((file) => ({
          type: "file" as const,
          key: file.id,
          file,
        })),
      ]
    : Array.from({ length: ATTACH_GRID_VISIBLE_SLOTS }, (_, index) => {
        if (index < visibleUploadTasks.length) {
          const task = visibleUploadTasks[index]!;

          return {
            type: "upload" as const,
            key: task.id,
            task,
          };
        }

        const file = items[index - visibleUploadTasks.length];

        return {
          type: file ? ("file" as const) : ("placeholder" as const),
          key: file?.id ?? `placeholder-${index}`,
          file,
        };
      });

  return (
    <div className={ATTACH_GRID_CLASS}>
      {slots.map((slot) => {
        if (slot.type === "upload") {
          return (
            <AttachUploadTaskCell
              key={slot.key}
              task={slot.task}
              onCancel={(taskId) => onCancelUpload?.(taskId)}
            />
          );
        }

        if (slot.type === "file" && slot.file) {
          return (
            <MemoAttachFileCell
              key={slot.key}
              file={slot.file}
              checked={selectedSet.has(slot.file.id)}
              picked={pickedId === slot.file.id}
              selectionVisible={selectionVisible}
              disabled={disabled}
              owned={slot.file.owned !== false}
              onToggleSelect={onToggleSelect}
              onPick={onPick}
              onDelete={onDelete}
              onReplace={onReplace}
              onMove={onMove}
              onRename={onRename}
            />
          );
        }

        return <AttachPlaceholderCell key={slot.key} />;
      })}
    </div>
  );
}
